import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { Modal } from "app/components/Modal";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { valueForOnlyNumericInputTextFields } from "utils/helpers";

import {
  selectIsAddModalOpen,
  selectTireTypeForm,
  selectTireTypeSaving,
} from "../../redux/selector";
import { tireTypeActions } from "../../redux/slice";
import { FIELD_KIND, FieldKind, TireTypeForm } from "../../types";
import { useStyles } from "./styles";

type FieldConfig = {
  key: keyof TireTypeForm;
  label: string;
};

const isNumberField = (name: keyof TireTypeForm) =>
  FIELD_KIND[name] === FieldKind.Number;

const normalizeTireTypeFieldValue = (
  name: keyof TireTypeForm,
  value: string,
) => {
  if (isNumberField(name)) {
    return valueForOnlyNumericInputTextFields(value);
  }
  return value;
};

const FIELDS: FieldConfig[] = [
  { key: "description", label: "DESCRIPTION" },
  { key: "code", label: "CODE" },
];

const TIRE_TYPE_MODAL = "TIRE_TYPE.MODAL";

const AddTireTypeModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectTireTypeForm);
  const saving = useSelector(selectTireTypeSaving);

  const onClose = () => {
    if (saving) return;
    dispatch(tireTypeActions.closeAddModal());
  };

  const setField = (name: keyof TireTypeForm, value: string) =>
    dispatch(
      tireTypeActions.setFormField({
        name,
        value: normalizeTireTypeFieldValue(name, value),
      }),
    );

  const isValid =
    Boolean(form.description.trim()) && Boolean(form.code.trim());

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>
            {t(`${TIRE_TYPE_MODAL}.ADD_TITLE`)}
          </Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {FIELDS.map(({ key, label }) => (
            <Box key={key} className={styles.field}>
              <Typography className={styles.label}>
                {t(`${TIRE_TYPE_MODAL}.FIELDS.${label}`)}
              </Typography>
              <TextField
                className={styles.input}
                fullWidth
                variant="outlined"
                placeholder={t(`${TIRE_TYPE_MODAL}.PLACEHOLDERS.ENTER_${label}`)}
                value={form[key]}
                disabled={saving}
                InputProps={{ notched: false }}
                {...(isNumberField(key) ? { inputMode: "numeric" as const } : {})}
                onChange={(event) => setField(key, event.target.value)}
              />
            </Box>
          ))}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={saving}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!isValid || saving}
            onClick={() => dispatch(tireTypeActions.addTireType())}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddTireTypeModal;
